/**
 * A profile as arithmetic, a source's (D52) or a regulator's (D65): a staircase's levels, how long a
 * profile runs, how far from ambient it goes, and where it first reaches a
 * value. Apart from the preview and the operations so both can use it.
 */
import type { Profile } from "../design/protocol";

const round = (value: number) => String(Number(value.toPrecision(3)));

export function secondsText(seconds: number): string {
  if (seconds >= 3600) return `${round(seconds / 3600)} h`;
  if (seconds >= 120) return `${round(seconds / 60)} min`;
  return `${round(seconds)} s`;
}

/** A staircase's levels, in order: its start, each increment towards its end, and its end. */
export function profileLevels(profile: Profile): number[] {
  const { start, end, increment } = profile;
  if (profile.mode !== "stairs" || start == null || end == null || !increment) return [];
  const count = Math.min(Math.floor(Math.abs(end - start) / increment + 1e-9), 500);
  const direction = end >= start ? 1 : -1;
  const levels = Array.from({ length: count + 1 }, (_, index) => start + direction * increment * index);
  if (Math.abs(levels.at(-1)! - end) > 1e-9 * Math.max(1, Math.abs(end))) levels.push(end);
  return levels;
}

/** How long a profile runs to its last level or segment (seconds). */
export function profileSeconds(profile: Profile): number | null {
  if (profile.mode === "stairs") {
    const levels = profileLevels(profile);
    return levels.length && profile.hold ? levels.length * profile.hold : null;
  }
  return (profile.segments ?? []).reduce((sum, segment) => sum + segment.seconds, 0);
}

/** Every value a profile passes through, in order. */
export function profileValues(profile: Profile): number[] {
  return profile.mode === "stairs" ? profileLevels(profile) : (profile.segments ?? []).flatMap((segment) => [segment.from_value, segment.to_value]);
}

/** Its furthest from ambient: the highest pressure, deepest vacuum, fastest flow. */
export function profilePeak(profile: Profile): number | null {
  const values = profileValues(profile);
  if (values.length === 0) return null;
  return values.reduce((far, value) => (Math.abs(value) > Math.abs(far) ? value : far));
}

/** Where a profile first reaches `threshold` (its magnitude): the level or time, and when. */
export function profileReaches(profile: Profile, threshold: number): { value: number; seconds: number } | null {
  if (profile.mode === "stairs") {
    const at = profileLevels(profile).findIndex((level) => Math.abs(level) >= threshold);
    return at < 0 ? null : { value: profileLevels(profile)[at]!, seconds: at * (profile.hold ?? 0) };
  }
  let elapsed = 0;
  for (const segment of profile.segments ?? []) {
    const [from, to] = [Math.abs(segment.from_value), Math.abs(segment.to_value)];
    if (from >= threshold) return { value: segment.from_value, seconds: elapsed };
    if (to >= threshold) {
      const fraction = (threshold - from) / (to - from);
      const sign = segment.to_value < 0 ? -1 : 1;
      return { value: sign * threshold, seconds: elapsed + fraction * segment.seconds };
    }
    elapsed += segment.seconds;
  }
  return null;
}
